import React from "react";
import { ShieldCheck, Layers, LineChart, ArrowRight } from "lucide-react";

function Container({ children, className = "" }) {
  return <div className={`mx-auto w-full max-w-6xl px-4 ${className}`}>{children}</div>;
}

function Card({ title, desc, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-950 text-white">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-base font-semibold text-slate-900">{title}</div>
          <p className="mt-1 text-sm leading-6 text-slate-600">{desc}</p>
        </div>
      </div>
    </div>
  );
}

export default function Investments() {
  return (
    <div className="min-h-screen bg-white">
      <Container className="py-10">
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-950">
          <ShieldCheck className="h-4 w-4" />
          Investments
        </div>

        <h1 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl text-slate-900">
          Investing built on clarity & discipline
        </h1>
        <p className="mt-2 max-w-2xl text-slate-600">
          Present your approach like a premium brand: simple, transparent, and confidence-building.
        </p>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <Card
            icon={Layers}
            title="Multi-asset allocation"
            desc="Diversify across equity, debt, and cash based on risk profile and time horizon."
          />
          <Card
            icon={LineChart}
            title="Benchmark tracking"
            desc="Compare performance against relevant benchmarks (NIFTY50 and debt indices)."
          />
          <Card
            icon={ShieldCheck}
            title="Risk alignment"
            desc="Investor profiling to map goals, comfort, and timeline before recommendations."
          />
          <div className="rounded-3xl border border-emerald-900/10 bg-emerald-950 p-6 text-white shadow-sm">
            <div className="text-sm font-semibold text-white/90">Next step</div>
            <div className="mt-2 text-2xl font-semibold">Build your plan</div>
            <p className="mt-2 text-sm leading-6 text-white/80">
              Add SIP Builder + portfolio baskets to turn this into a real product funnel.
            </p>
            <a
              href="/sips"
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-emerald-950"
            >
              Explore SIPs <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="mt-10 rounded-3xl border border-emerald-900/10 bg-white p-7 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900">Risk profiles (example)</h2>
          <p className="mt-2 text-sm text-slate-600">
            Add these as cards on the website to look premium and structured.
          </p>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {[
              { t: "Conservative", d: "Lower volatility focus with higher debt allocation." },
              { t: "Balanced", d: "Mix of equity and debt for steady growth." },
              { t: "Growth", d: "Higher equity allocation for long-term wealth creation." },
            ].map((x) => (
              <div key={x.t} className="rounded-2xl bg-slate-50 p-5">
                <div className="font-semibold text-slate-900">{x.t}</div>
                <div className="mt-1 text-sm text-slate-600">{x.d}</div>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-6 text-xs text-slate-500">
          Note: Add compliance text based on your business model (RIA/MFD). Avoid promising guaranteed returns.
        </p>
      </Container>
    </div>
  );
}
